import {AICompanyKpiTemplateFields, TemplateType} from 'common/report/dashboards/types';

import {
  BenchmarkKPITemplateFields,
  ECommerceTemplateFields,
  KPITemplateFields,
  LifecycleCohortAnalysisTemplateFields,
  mapTemplateFieldToEventType,
  MarketingKPITemplateFields,
  ProductLaunchTemplateFields,
  TemplateField,
  WebAnalyticsTemplateFields,
} from './types';

/**
 * An event-shaped query sent to the similar-events endpoint to find the project's events that best
 * fit a board-template event field. `name` doubles as the field's standardized event type.
 */
export type SuggestedEventQuery = {
  name: string;
  description: string;
};

/**
 * Keyed by the standardized event type that `mapTemplateFieldToEventType` returns for a template field.
 * Template fields that share a type (e.g. the KPI, Benchmark and AI "Sign Up" fields) share one query.
 * The descriptions are query text for the embedding model, not UI copy, so they stay in English.
 */
const SUGGESTED_EVENT_QUERIES: Record<string, SuggestedEventQuery> = {
  [KPITemplateFields.SIGN_UP]: {
    name: KPITemplateFields.SIGN_UP,
    description:
      `User registration and account creation events where new users join the platform, create accounts, ` +
      `verify their identity, complete signup forms, and establish their initial user profile. Includes email ` +
      `verification, social login, trial account setup, and initial account activation. Keywords: registered, ` +
      `created, signup, joined, verified, onboarded, new user, welcome, confirmation.`,
  },
  [KPITemplateFields.VALUE_MOMENT]: {
    name: KPITemplateFields.VALUE_MOMENT,
    description:
      `Completion and success events where users finish important tasks: completing assessments, onboarding, ` +
      `checkout flows, verification, profile setup, creating campaigns or templates, finishing tours, surveys and ` +
      `setup wizards. Keywords: complete, completed, finished, create, created, assessment, onboarding, ` +
      `verification, profile, setup, campaign, template, tour, survey.`,
  },
  [MarketingKPITemplateFields.ACTIVATION_EVENT]: {
    name: MarketingKPITemplateFields.ACTIVATION_EVENT,
    description:
      `First-time user activation events: first time app opens, first time home screen views, first use of ` +
      `filters or smart lists, activating devices, kiosks or services, activation attempts, getting started ` +
      `flows, tutorials completed, and initial authorization or onboarding steps. Keywords: activate, ` +
      `activation, activated, first time, get started, getting started, tutorial, onboarding step, initial, ` +
      `authorization.`,
  },
  [MarketingKPITemplateFields.CONVERSION_EVENT]: {
    name: MarketingKPITemplateFields.CONVERSION_EVENT,
    description:
      `Monetization events where users transition from free to paid, upgrade subscriptions, complete payment ` +
      `transactions, purchase plans or add-ons, and make financial commitments: trial conversions, subscription ` +
      `starts, plan upgrades, payment processing, contract signings. Keywords: upgraded, paid, subscription, ` +
      `payment, checkout, purchased, billing, converted, premium, pro, enterprise.`,
  },
  [WebAnalyticsTemplateFields.PAGE_LOADED_EVENT]: {
    name: WebAnalyticsTemplateFields.PAGE_LOADED_EVENT,
    description:
      `Page view events tracking when users navigate to and load pages, sections, or views within the ` +
      `application: homepage views, dashboard loads, settings access, and any page or screen being displayed. ` +
      `Keywords: viewed, loaded, opened, accessed, page, visited, displayed, shown.`,
  },
  [ProductLaunchTemplateFields.NEW_FEATURE]: {
    name: ProductLaunchTemplateFields.NEW_FEATURE,
    description:
      `Events tracking when users discover, enable, or first interact with new product features, beta ` +
      `functionality, advanced tools, or recently released capabilities: beta tools, advanced editors, bulk ` +
      `actions, calendar views, dark mode, collaboration features. Keywords: new, beta, enabled, activated, ` +
      `first time, discovered, toggled, configured feature, advanced.`,
  },
  [ProductLaunchTemplateFields.FEATURE_VALUE_MOMENT]: {
    name: ProductLaunchTemplateFields.FEATURE_VALUE_MOMENT,
    description:
      `Events demonstrating users getting value from product features through successful outcomes: items saved, ` +
      `content edited and saved, documents downloaded or exported, data synced, errors corrected, integrations ` +
      `configured, quotes obtained, bulk processing completed, dashboards duplicated. Keywords: saved, ` +
      `downloaded, exported, synced, success, corrected, undo, duplicate, integration, quote, bulk, editor, widget.`,
  },
  [AICompanyKpiTemplateFields.AI_START_AGENT_EVENT_FIELD]: {
    name: AICompanyKpiTemplateFields.AI_START_AGENT_EVENT_FIELD,
    description:
      `Events when users open, activate, or initiate AI-powered features, chatbots, virtual assistants, copilots, ` +
      `or intelligent tools: opening chat windows, activating AI assistants, launching smart compose, enabling ` +
      `copilots, displaying AI panels. Keywords: AI, opened, launched, activated, started, enabled, assistant, ` +
      `chatbot, copilot, smart, intelligent.`,
  },
  [AICompanyKpiTemplateFields.AI_SUBMIT_PROMPT_EVENT_FIELD]: {
    name: AICompanyKpiTemplateFields.AI_SUBMIT_PROMPT_EVENT_FIELD,
    description:
      `Events when users submit questions, commands, instructions, or requests to AI systems, chatbots, or ` +
      `intelligent assistants: questions asked, commands submitted, prompts entered, generation requests, natural ` +
      `language queries. Keywords: sent, submitted, asked, query, prompt, question, command, requested, ` +
      `instruction, message to AI.`,
  },
  [AICompanyKpiTemplateFields.AI_RESPONSE_SENT_EVENT_FIELD]: {
    name: AICompanyKpiTemplateFields.AI_RESPONSE_SENT_EVENT_FIELD,
    description:
      `Events when AI systems, chatbots, or intelligent assistants generate and deliver responses, answers, ` +
      `suggestions, or content to users: answers generated, responses delivered, suggestions provided, content ` +
      `created, recommendations made. Keywords: generated, delivered, response, reply, answer, completed, ` +
      `provided, created, returned, AI output.`,
  },
  [AICompanyKpiTemplateFields.AI_API_ERROR_EVENT_FIELD]: {
    name: AICompanyKpiTemplateFields.AI_API_ERROR_EVENT_FIELD,
    description:
      `Technical error events including failed API requests, server errors, authentication failures, rate ` +
      `limiting, timeouts, and system malfunctions: rate limits exceeded, authentication failures, server ` +
      `timeouts, invalid API keys, 400/500 errors, service unavailability. Keywords: error, failed, exceeded, ` +
      `timeout, invalid, unavailable, unauthorized, not found, connection error, quota.`,
  },
  [AICompanyKpiTemplateFields.AI_USER_FEEDBACK_EVENT_FIELD]: {
    name: AICompanyKpiTemplateFields.AI_USER_FEEDBACK_EVENT_FIELD,
    description:
      `Events when users provide opinions, ratings, reviews, comments, suggestions, bug reports, or any form of ` +
      `feedback about the product: thumbs up or down, ratings submitted, feedback forms completed, bug reports, ` +
      `feature requests, surveys, satisfaction scores. Keywords: feedback, rating, review, comment, submitted, ` +
      `reported, survey, thumbs, suggestion, voted, NPS.`,
  },
  [AICompanyKpiTemplateFields.AI_AGENT_DISMISSED_EVENT_FIELD]: {
    name: AICompanyKpiTemplateFields.AI_AGENT_DISMISSED_EVENT_FIELD,
    description:
      `Modal and dialog dismissal events including closing modals, canceling dialogs, dismissing banners, exiting ` +
      `webviews or fullscreen, skipping intros or ads, closing copilot or assistant windows, and dismissing ` +
      `experiences or surveys. Keywords: cancel, close, closed, dismiss, dismissed, modal, dialog, exit, skip, ` +
      `webview, fullscreen, banner, copilot, assistant.`,
  },
  [ECommerceTemplateFields.PURCHASE_EVENT]: {
    name: ECommerceTemplateFields.PURCHASE_EVENT,
    description:
      `E-commerce transaction events where users complete purchases, buy products, process payments for items, ` +
      `finalize orders, and acquire goods or services: orders placed, products bought, transactions successful, ` +
      `payments processed, one-time purchases of items or credits. Keywords: purchased, bought, completed, ` +
      `transaction, order, payment processed, sale, acquired, finalized, confirmed purchase.`,
  },
  [ECommerceTemplateFields.ADD_TO_CART_EVENT]: {
    name: ECommerceTemplateFields.ADD_TO_CART_EVENT,
    description:
      `E-commerce events when users add products or items to their shopping cart, basket, or bag in preparation ` +
      `for a purchase: items added to cart, products placed in basket, cart updates, quantity changes, products ` +
      `moved to shopping bag. Keywords: added to cart, cart, basket, shopping bag, item added, quantity ` +
      `increased, selected for purchase, cart updated.`,
  },
  [ECommerceTemplateFields.PRODUCT_VIEWED_EVENT]: {
    name: ECommerceTemplateFields.PRODUCT_VIEWED_EVENT,
    description:
      `E-commerce browsing events when users view product details, examine item information, open product ` +
      `pages, or look at product images and specifications: product detail page views, item clicks, image ` +
      `views, specification examination. Keywords: viewed, product page, detail, clicked, examined, item page, ` +
      `opened, specs, description, image viewed.`,
  },
  [ECommerceTemplateFields.AD_SPEND_EVENT]: {
    name: ECommerceTemplateFields.AD_SPEND_EVENT,
    description:
      `Advertising and marketing events tracking ad impressions, clicks, conversions, campaign performance, pixel ` +
      `fires, and marketing attribution: ad impressions, advertisement clicks, campaign views, banner displays, ` +
      `sponsored content, pixel fires, conversion tracking. Keywords: ad, advertisement, impression, clicked, ` +
      `campaign, banner, sponsored, marketing, pixel, retargeting, attribution.`,
  },
};

export function getSuggestedEventQuery(templateField: TemplateField): SuggestedEventQuery | undefined {
  return SUGGESTED_EVENT_QUERIES[mapTemplateFieldToEventType(templateField)];
}

/**
 * The event fields each template's RHS panel renders with a Suggested Events section, i.e. the fields
 * it passes to `preselectedEventsSection` in `common/widgets/rhs-panel/index.tsx`.
 */
const TEMPLATE_SUGGESTED_EVENT_FIELDS: Partial<Record<TemplateType, TemplateField[]>> = {
  [TemplateType.KPI]: [KPITemplateFields.SIGN_UP, KPITemplateFields.VALUE_MOMENT],
  [TemplateType.FEATURE_LAUNCH]: [
    ProductLaunchTemplateFields.NEW_FEATURE,
    ProductLaunchTemplateFields.FEATURE_VALUE_MOMENT,
  ],
  [TemplateType.LIFECYCLE_COHORT_ANALYSIS]: [LifecycleCohortAnalysisTemplateFields.VALUE_MOMENT],
  [TemplateType.MARKETING_KPI]: [
    MarketingKPITemplateFields.ACTIVATION_EVENT,
    MarketingKPITemplateFields.CONVERSION_EVENT,
  ],
  [TemplateType.E_COMMERCE]: [ECommerceTemplateFields.SIGN_UP_EVENT, ECommerceTemplateFields.PRODUCT_VIEWED_EVENT],
  [TemplateType.WEB_ANALYTICS]: [WebAnalyticsTemplateFields.PAGE_LOADED_EVENT],
  [TemplateType.BENCHMARK_KPI]: [BenchmarkKPITemplateFields.SIGN_UP, BenchmarkKPITemplateFields.VALUE_MOMENT],
  [TemplateType.AI_COMPANY_KPI]: [
    AICompanyKpiTemplateFields.AI_START_AGENT_EVENT_FIELD,
    AICompanyKpiTemplateFields.AI_RESPONSE_SENT_EVENT_FIELD,
    AICompanyKpiTemplateFields.AI_API_ERROR_EVENT_FIELD,
    AICompanyKpiTemplateFields.AI_USER_FEEDBACK_EVENT_FIELD,
    AICompanyKpiTemplateFields.AI_AGENT_DISMISSED_EVENT_FIELD,
    AICompanyKpiTemplateFields.AI_SIGN_UP_EVENT_FIELD,
    AICompanyKpiTemplateFields.AI_CONVERSION_EVENT_FIELD,
  ],
};

export function getTemplateSuggestedEventFields(templateType: TemplateType): TemplateField[] {
  return TEMPLATE_SUGGESTED_EVENT_FIELDS[templateType] ?? [];
}
